import { useState, useEffect } from "react";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import {
  Building2,
  Users,
  MessageSquare,
  BarChart3,
  ArrowLeft,
  Settings,
  TrendingUp,
  Clock,
  Send,
  Eye,
  Loader2,
  Plus,
  UserPlus,
  Edit,
  Globe,
  Phone,
  Mail,
  ShoppingBag,
  Inbox,
  BadgeCheck,
  Trash2,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import CatalogManager from "../components/CatalogManager";
import TeamInboxView from "../components/TeamInboxView";
import PremiumUpgradeDialog from "../components/PremiumUpgradeDialog";
import VerifiedBadge from "../components/VerifiedBadge";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const BusinessDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("overview");
  const [loading, setLoading] = useState(true);
  const [businessInfo, setBusinessInfo] = useState({
    name: "",
    description: "",
    phone: "",
    website: "",
    address: "",
  });
  const [stats, setStats] = useState({
    totalMessages: 0,
    totalConversations: 0,
    activeUsers: 0,
    responseTime: "0",
  });
  const [teamMembers, setTeamMembers] = useState([]);

  useEffect(() => {
    fetchBusinessInfo();
    fetchStats();
  }, []);

  const fetchBusinessInfo = async () => {
    try {
      const response = await fetch(`${API}/user/category`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        if (data.business_info) {
          setBusinessInfo(data.business_info);
        }
      }
    } catch (error) {
      console.error("Error fetching business info:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API}/conversations`, { credentials: "include" });
      if (response.ok) {
        const conversations = await response.json();
        setStats({
          totalConversations: conversations.length,
          totalMessages: conversations.reduce((acc, c) => acc + (c.message_count || 0), 0),
          activeUsers: new Set(conversations.flatMap((c) => c.participants || [])).size,
          responseTime: "< 5 min",
        });
      }
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };

  const updateBusinessInfo = async () => {
    try {
      const response = await fetch(`${API}/user/category`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          category: "business",
          business_name: businessInfo.name,
          business_description: businessInfo.description,
        }),
      });

      if (response.ok) {
        toast.success("Información actualizada");
      }
    } catch (error) {
      toast.error("Error al actualizar información");
    }
  };

  return (
    <div className="min-h-screen bg-[#0e1621]">
      {/* Header */}
      <div className="bg-[#17212b] border-b border-[#232e3c] px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate("/chat")}
              className="text-slate-400 hover:text-white"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold text-white flex items-center gap-2">
                <Building2 className="w-6 h-6 text-blue-500" />
                Dashboard de Negocio
              </h1>
              <p className="text-sm text-slate-400">Gestiona tu negocio en OkChat</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Avatar>
              <AvatarImage src={user?.picture} />
              <AvatarFallback className="bg-blue-600 text-white">
                {user?.name?.charAt(0) || "N"}
              </AvatarFallback>
            </Avatar>
            <div className="text-right">
              <p className="text-white font-medium">{businessInfo.name || user?.name}</p>
              <p className="text-xs text-slate-400">Negocio</p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto p-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-[#17212b] border border-[#232e3c]">
            <TabsTrigger value="overview" className="data-[state=active]:bg-blue-600">
              <BarChart3 className="w-4 h-4 mr-2" />
              Resumen
            </TabsTrigger>
            <TabsTrigger value="messages" className="data-[state=active]:bg-blue-600">
              <MessageSquare className="w-4 h-4 mr-2" />
              Mensajes
            </TabsTrigger>
            <TabsTrigger value="team" className="data-[state=active]:bg-blue-600">
              <Users className="w-4 h-4 mr-2" />
              Equipo
            </TabsTrigger>
            <TabsTrigger value="catalog" className="data-[state=active]:bg-blue-600">
              <ShoppingBag className="w-4 h-4 mr-2" />
              Catálogo
            </TabsTrigger>
            <TabsTrigger value="inbox" className="data-[state=active]:bg-blue-600">
              <Inbox className="w-4 h-4 mr-2" />
              Bandeja
            </TabsTrigger>
            <TabsTrigger value="membership" className="data-[state=active]:bg-blue-600">
              <BadgeCheck className="w-4 h-4 mr-2" />
              Membresía
            </TabsTrigger>
            <TabsTrigger value="settings" className="data-[state=active]:bg-blue-600">
              <Settings className="w-4 h-4 mr-2" />
              Configuración
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="mt-6">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
              </div>
            ) : (
              <>
                {/* Stats Grid */}
                <div className="grid md:grid-cols-4 gap-4 mb-6">
                  {[
                    { label: "Conversaciones", value: stats.totalConversations, icon: MessageSquare, color: "text-blue-400", bg: "bg-blue-600/20" },
                    { label: "Mensajes totales", value: stats.totalMessages, icon: Send, color: "text-green-400", bg: "bg-green-600/20" },
                    { label: "Usuarios activos", value: stats.activeUsers, icon: Users, color: "text-purple-400", bg: "bg-purple-600/20" },
                    { label: "Tiempo de respuesta", value: stats.responseTime, icon: Clock, color: "text-orange-400", bg: "bg-orange-600/20" },
                  ].map((stat, idx) => (
                    <Card key={idx} className="bg-[#17212b] border-[#232e3c]">
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-slate-400 text-sm">{stat.label}</p>
                            <p className={`text-2xl font-bold ${stat.color}`}>{stat.value}</p>
                          </div>
                          <div className={`p-3 rounded-lg ${stat.bg}`}>
                            <stat.icon className={`w-6 h-6 ${stat.color}`} />
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>

                {/* Business Card */}
                <Card className="bg-[#17212b] border-[#232e3c]">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <Building2 className="w-5 h-5 text-blue-500" />
                      Perfil de Negocio
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-start gap-6">
                      <Avatar className="w-24 h-24">
                        <AvatarImage src={user?.picture} />
                        <AvatarFallback className="bg-blue-600 text-white text-2xl">
                          {businessInfo.name?.charAt(0) || user?.name?.charAt(0) || "N"}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1">
                        <h3 className="text-xl text-white font-bold">{businessInfo.name || "Mi Negocio"}</h3>
                        <p className="text-slate-400 mt-1">{businessInfo.description || "Sin descripción"}</p>
                        <div className="flex flex-wrap gap-4 mt-4">
                          {businessInfo.phone && (
                            <span className="text-slate-300 flex items-center gap-1 text-sm">
                              <Phone className="w-4 h-4" /> {businessInfo.phone}
                            </span>
                          )}
                          {businessInfo.website && (
                            <span className="text-slate-300 flex items-center gap-1 text-sm">
                              <Globe className="w-4 h-4" /> {businessInfo.website}
                            </span>
                          )}
                        </div>
                      </div>
                      <Button
                        variant="outline"
                        className="border-blue-600 text-blue-400"
                        onClick={() => setActiveTab("settings")}
                      >
                        <Edit className="w-4 h-4 mr-2" />
                        Editar
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </>
            )}
          </TabsContent>

          {/* Messages Tab */}
          <TabsContent value="messages" className="mt-6">
            <Card className="bg-[#17212b] border-[#232e3c]">
              <CardHeader>
                <CardTitle className="text-white">Mensajes Recientes</CardTitle>
                <CardDescription className="text-slate-400">
                  Últimas conversaciones con tus clientes
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-center py-8">
                  <MessageSquare className="w-16 h-16 text-slate-600 mx-auto mb-4" />
                  <p className="text-slate-400">Gestiona tus mensajes desde el chat principal</p>
                  <Button
                    className="mt-4 bg-blue-600 hover:bg-blue-700"
                    onClick={() => navigate("/chat")}
                  >
                    Ir al Chat
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Team Tab */}
          <TabsContent value="team" className="mt-6">
            <Card className="bg-[#17212b] border-[#232e3c]">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-white">Equipo</CardTitle>
                    <CardDescription className="text-slate-400">
                      Gestiona los miembros de tu equipo
                    </CardDescription>
                  </div>
                  <Button className="bg-blue-600 hover:bg-blue-700">
                    <UserPlus className="w-4 h-4 mr-2" />
                    Invitar miembro
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-center py-8">
                  <Users className="w-16 h-16 text-slate-600 mx-auto mb-4" />
                  <p className="text-slate-400">Aún no tienes miembros en tu equipo</p>
                  <p className="text-sm text-slate-500 mt-2">
                    Invita a colaboradores para gestionar las conversaciones de tu negocio
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Catalog Tab */}
          <TabsContent value="catalog" className="mt-6">
            <CatalogManager />
          </TabsContent>

          {/* Team Inbox Tab */}
          <TabsContent value="inbox" className="mt-6">
            <TeamInboxView />
          </TabsContent>

          {/* Membership Tab */}
          <TabsContent value="membership" className="mt-6">
            <MembershipPanel user={user} />
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings" className="mt-6">
            <Card className="bg-[#17212b] border-[#232e3c]">
              <CardHeader>
                <CardTitle className="text-white">Información del Negocio</CardTitle>
                <CardDescription className="text-slate-400">
                  Actualiza la información de tu negocio
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-300">Nombre del negocio</Label>
                    <Input
                      value={businessInfo.name}
                      onChange={(e) => setBusinessInfo({ ...businessInfo, name: e.target.value })}
                      className="bg-[#0e1621] border-[#232e3c] text-white mt-1"
                      placeholder="Mi Negocio"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-300">Teléfono</Label>
                    <Input
                      value={businessInfo.phone}
                      onChange={(e) => setBusinessInfo({ ...businessInfo, phone: e.target.value })}
                      className="bg-[#0e1621] border-[#232e3c] text-white mt-1"
                      placeholder="+52 555 123 4567"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-slate-300">Sitio web</Label>
                  <Input
                    value={businessInfo.website}
                    onChange={(e) => setBusinessInfo({ ...businessInfo, website: e.target.value })}
                    className="bg-[#0e1621] border-[#232e3c] text-white mt-1"
                    placeholder="https://minegocio.com"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Descripción</Label>
                  <textarea
                    value={businessInfo.description}
                    onChange={(e) => setBusinessInfo({ ...businessInfo, description: e.target.value })}
                    className="w-full bg-[#0e1621] border border-[#232e3c] text-white rounded-md p-3 mt-1 min-h-[100px]"
                    placeholder="Describe tu negocio..."
                  />
                </div>
                <Button className="bg-blue-600 hover:bg-blue-700" onClick={updateBusinessInfo}>
                  Guardar cambios
                </Button>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

// Membership / OkChat for Business panel
const MembershipPanel = ({ user }) => {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [showUpgrade, setShowUpgrade] = useState(false);

  const token = () => localStorage.getItem("session_token");
  const headers = () => ({ "Content-Type": "application/json", ...(token() ? { Authorization: `Bearer ${token()}` } : {}) });

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/subscriptions/me`, { headers: headers() });
      if (res.ok) setInfo(await res.json());
    } catch (e) {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    if (params.get("subscription") === "success" && sessionId) {
      fetch(`${API}/subscriptions/business/sync`, {
        method: "POST",
        headers: headers(),
        body: JSON.stringify({ session_id: sessionId }),
      })
        .then(() => {
          // Clean URL so refresh doesn't re-sync.
          const clean = window.location.pathname;
          window.history.replaceState({}, "", clean);
          load();
        })
        .catch(() => null);
    }
  }, []);

  const cancel = async () => {
    if (!window.confirm("¿Cancelar la membresía OkChat for Business?")) return;
    setWorking(true);
    try {
      const res = await fetch(`${API}/subscriptions/business/cancel`, { method: "POST", headers: headers() });
      if (res.ok) {
        toast.success("Membresía cancelada");
        await load();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "No se pudo cancelar");
      }
    } finally {
      setWorking(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 text-slate-400">
        <Loader2 className="w-6 h-6 animate-spin mr-2" /> Cargando…
      </div>
    );
  }

  const isActive = info?.tier === "business" && info?.status === "active";

  return (
    <div className="space-y-6">
      <Card className="bg-[#17212b] border-[#232e3c]">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <BadgeCheck className="w-5 h-5 text-blue-400" />
            OkChat for Business
            {isActive && <VerifiedBadge size={20} />}
          </CardTitle>
          <CardDescription className="text-slate-400">
            Verificación azul, prioridad de soporte y herramientas premium para tu negocio.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-4 rounded-lg bg-[#0e1621] border border-[#232e3c]">
            <div>
              <p className="text-sm text-slate-400">Estado actual</p>
              <p className="text-lg font-semibold text-white capitalize">
                {info?.tier === "business" ? "Business" : "Gratuito"}
                {info?.status ? ` · ${info.status}` : ""}
              </p>
            </div>
            <p className="text-2xl font-bold text-blue-400">$30 MXN<span className="text-sm text-slate-400">/mes</span></p>
          </div>

          {isActive ? (
            <Button onClick={cancel} disabled={working} variant="destructive" className="w-full">
              {working ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Cancelar membresía
            </Button>
          ) : (
            <Button onClick={() => setShowUpgrade(true)} className="w-full bg-blue-600 hover:bg-blue-700">
              <BadgeCheck className="w-4 h-4 mr-2" /> Activar OkChat for Business
            </Button>
          )}
        </CardContent>
      </Card>
      <PremiumUpgradeDialog open={showUpgrade} onOpenChange={setShowUpgrade} />
    </div>
  );
};

export default BusinessDashboard;
